<script setup lang="ts">
import { openConsentPanel } from '@/lib/consent';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '@fontsource-variable/manrope';
import '@fontsource/dm-mono/400.css';
import '@fontsource/dm-mono/500.css';
import { AI_TRIAL_LIMITS, BRAND_NAME, LEGAL, PLANS, TRIAL_DAYS, TRIAL_INVOICE_LIMIT } from '@bill-pulse/shared';
import { api } from '@/lib/api';
import { DEFAULT_DESCRIPTION, setJsonLd, SITE_URL } from '@/lib/seo';
import { GUIDES } from '@/content/guides';
import { useAuthStore } from '@/stores/auth';
import { theme, toggleTheme } from '@/lib/theme';
import BrandMark from '@/components/BrandMark.vue';
import LandingIcon from '@/components/LandingIcon.vue';
import NavIcon from '@/components/NavIcon.vue';

/**
 * Page d'accueil publique, inspirée de la maquette Figma fournie (structure,
 * rythme, animations), aux couleurs de Yakafa et au contenu vrai : ni chiffres
 * d'usage, ni avis, ni tarif inventés. L'IA y est mise en avant parce que c'est
 * ce qui distingue l'offre ; ses exemples décrivent ce que fait réellement le
 * produit. Polices embarquées (fontsource) : la CSP de production bloquerait
 * Google Fonts.
 */
gsap.registerPlugin(ScrollTrigger);

const auth = useAuthStore();
const root = ref<HTMLElement | null>(null);
const menuOpen = ref(false);
const openFaq = ref(0);

/** Les inscriptions peuvent être fermées par un admin : l'essai n'est alors pas proposé. */
const registrationOpen = ref(false);

// Connecté : on mène à l'espace. Sinon, à l'essai gratuit si les inscriptions
// sont ouvertes — à la connexion dans le cas contraire, jamais vers un leurre.
const primaryCta = computed(() => {
  if (auth.user) return { to: '/dashboard', label: 'Ouvrir mon tableau de bord' };
  if (registrationOpen.value) return { to: '/login?mode=register', label: 'Commencer l’essai gratuit' };
  return { to: '/login', label: 'Se connecter' };
});

/** Ce que fait l'IA, avec une consigne et un résultat conformes au produit réel. */
const aiFeatures = [
  {
    icon: 'quote' as const,
    title: 'Un devis à partir du brief client, écrit ou vocal',
    prompt: '« Trois ateliers de 2 h en novembre, à Nantes, acompte de 30 %. » — tapé, collé ou dicté.',
    result: 'Lignes, conditions et notes rédigées ; vos prix viennent de votre catalogue, toute estimation est signalée.',
  },
  {
    icon: 'document' as const,
    title: 'Vos factures récurrentes, en une phrase',
    prompt: '« Cours du mois, du 26 au 24. Le 11 était férié et j’étais absente le 3. »',
    result: 'Les séances datées, désignations et prix repris de vos factures précédentes, jours exclus expliqués.',
  },
  {
    icon: 'camera' as const,
    title: 'Vos tickets photographiés, vos dépenses remplies',
    prompt: 'Une photo de ticket de caisse, même un peu froissé.',
    result: 'Fournisseur, date, TVA par taux et montant pré-remplis ; les champs douteux sont surlignés.',
  },
];

/** Fonctionnalités réelles ; `pro` = réservé à l'offre Pro (même règle que l'API). */
const features = [
  {
    number: '01',
    icon: 'send' as const,
    title: 'Envoi Peppol, suivi en direct',
    text: "Un SIRET suffit pour retrouver votre client. Un clic transmet la facture ; remise, litige ou paiement remontent d'eux-mêmes dans votre tableau de bord.",
  },
  {
    number: '02',
    icon: 'pen' as const,
    title: 'Devis signé sur le chantier',
    text: 'Votre client signe au doigt sur votre téléphone, ou accepte en ligne depuis le lien reçu. Preuve horodatée, copie signée envoyée par email.',
  },
  {
    number: '03',
    icon: 'card' as const,
    title: 'Acompte payé par carte',
    text: "À la signature, votre client règle l'acompte en ligne, directement sur votre compte Stripe. La facture d'acompte se crée toute seule et se déduit de la facture finale.",
    pro: true,
  },
  {
    number: '04',
    icon: 'calendar' as const,
    title: 'Planning et prise de rendez-vous',
    text: "Votre page de réservation propose des créneaux qui tiennent compte de vos trajets et de votre zone d'intervention. Rappel au client la veille, agenda synchronisé avec votre téléphone.",
    pro: true,
  },
  {
    number: '05',
    icon: 'bell' as const,
    title: 'Relances automatiques',
    text: 'Devis sans réponse, acompte en attente, facture échue : Yakafa relance poliment à votre place, jamais plus d’une fois par jour et par client.',
  },
  {
    number: '06',
    icon: 'user' as const,
    title: 'Professionnels et particuliers',
    text: 'Vos clients entreprises reçoivent leur facture sur le réseau Peppol ; vos clients particuliers, par email avec le PDF, et les mentions adaptées.',
  },
  {
    number: '07',
    icon: 'shield' as const,
    title: 'Conforme sans y penser',
    text: 'PDF Factur-X, mentions obligatoires, mention 293 B pour la franchise de TVA, factures transmises immuables, avoirs : la réforme est prise en charge.',
  },
  {
    number: '08',
    icon: 'chart' as const,
    title: 'Tableau de bord',
    text: 'Facturé, encaissé, dépenses, impayés, devis en attente et rendez-vous de la semaine : votre activité en un coup d’œil, avec un lien pour agir.',
  },
  {
    number: '09',
    icon: 'wallet' as const,
    title: 'Comptabilité simplifiée',
    text: 'Livre des recettes, registre des achats, export FEC pour votre comptable, et un repère pour savoir quand le régime réel deviendrait plus avantageux.',
  },
];

/** Grille tarifaire : tirée de `PLANS`, la même définition que celle qu'applique l'API. */
const offers = [
  {
    id: 'trial',
    name: 'Essai gratuit',
    price: '0 €',
    period: `pendant ${TRIAL_DAYS} jours`,
    items: [
      `${TRIAL_INVOICE_LIMIT} factures émises`,
      `${AI_TRIAL_LIMITS.invoice_draft} factures et ${AI_TRIAL_LIMITS.quote_draft} devis rédigés par l'IA`,
      `${AI_TRIAL_LIMITS.expense_scan} justificatifs lus par l'IA`,
      'Toutes les fonctionnalités, sans carte bancaire',
    ],
    highlight: false,
  },
  ...(['essential', 'pro', 'expert'] as const).map((id) => ({
    id,
    name: PLANS[id].label,
    price: `${PLANS[id].priceEur} €`,
    period: 'HT par mois',
    items: [
      `${PLANS[id].invoicesPerMonth} factures émises par mois`,
      `${PLANS[id].aiDraftsPerMonth} factures ou devis rédigés par l'IA`,
      `${PLANS[id].scansPerMonth} justificatifs lus par l'IA`,
      ...({
        essential: ['Devis signés, relance des factures échues, export FEC'],
        pro: ['Acompte payé par carte à la signature', 'Page de réservation et planning des rendez-vous', 'Relances automatiques des devis et acomptes', 'Accès gratuit pour votre expert-comptable'],
        expert: ['Tout le Pro', 'Votre numéro dédié : appels manqués transcrits et résumés par l\'IA', 'Demandes WhatsApp (texte, vocaux, photos) prêtes à devenir un devis', `${PLANS.expert.signaturesPerMonth} documents en signature électronique par mois`],
      } as const)[id],
    ],
    highlight: id === 'pro',
  })),
];

const faqs = [
  [
    'Suis-je concerné par la facturation électronique ?',
    "Oui, si vous facturez d'autres entreprises en France. Depuis le 1er septembre 2026, toutes doivent pouvoir recevoir des factures électroniques ; l'obligation d'émettre suit, jusqu'en septembre 2027 pour les PME et micro-entreprises.",
  ],
  [
    "L'IA peut-elle se tromper ?",
    "Oui, c'est pourquoi rien n'est enregistré ni envoyé sans votre relecture. Elle propose, vous validez : les montants sont recalculés par Yakafa, les prix estimés et les champs douteux sont surlignés, et l'explication de ses choix s'affiche à côté.",
  ],
  [
    'Que deviennent mes données envoyées à l’IA ?',
    "L'IA de Yakafa est Claude, d'Anthropic, utilisé via son API professionnelle : vos factures, devis et justificatifs servent à rédiger la proposition demandée et ne sont pas utilisés pour entraîner le modèle.",
  ],
  [
    "Qu'est-ce que le réseau Peppol ?",
    "Le réseau européen d'échange de factures électroniques. Yakafa y transmet vos factures en retrouvant votre client par son SIRET ; s'il n'y est pas encore, la facture lui parvient par email, au format Factur-X.",
  ],
  [
    'Je suis en franchise de TVA, est-ce géré ?',
    "Oui. Cochez « TVA non applicable » : la facture n'affiche que des montants HT et porte la mention de l'article 293 B du CGI.",
  ],
  [
    "Comment fonctionne l'essai gratuit ?",
    `Vous créez votre compte avec le SIRET de votre entreprise, sans carte bancaire. L'essai dure ${TRIAL_DAYS} jours et couvre jusqu'à ${TRIAL_INVOICE_LIMIT} factures émises, avec l'IA incluse : ${AI_TRIAL_LIMITS.invoice_draft} factures et ${AI_TRIAL_LIMITS.quote_draft} devis rédigés, ${AI_TRIAL_LIMITS.expense_scan} justificatifs lus. Vous choisissez ensuite l'offre Essentiel (${PLANS.essential.priceEur} € par mois), Pro (${PLANS.pro.priceEur} € par mois) ou Expert (${PLANS.expert.priceEur} € par mois).`,
  ],
  [
    'Je travaille surtout pour des particuliers, est-ce adapté ?',
    "Oui. Créez la fiche client en « Particulier » (sans SIRET) : la facture lui est envoyée par email avec le PDF, avec les mentions propres aux consommateurs. Les factures entre entreprises, elles, passent par le réseau Peppol.",
  ],
  [
    'Mes clients peuvent-ils prendre rendez-vous seuls ?',
    `Oui, avec l'offre Pro : vous publiez une page de réservation avec vos interventions, horaires et zone. Les créneaux proposés tiennent compte de vos trajets, le client reçoit un rappel la veille, et vos rendez-vous s'affichent dans l'agenda de votre téléphone.`,
  ],
  [
    'Comment fonctionne l’acompte en ligne ?',
    "Avec l'offre Pro, vous reliez votre propre compte Stripe. À la signature du devis, le client paie l'acompte par carte : l'argent arrive sur votre compte, sans commission Yakafa (seuls les frais Stripe s'appliquent), et la facture d'acompte est créée automatiquement.",
  ],
  [
    'Et si mon client refuse une facture ?',
    "Vous le voyez dans le suivi, avec le motif s'il est transmis. Une facture émise ne se modifie plus : elle se corrige par un avoir, comme l'exige la loi.",
  ],
];

let context: gsap.Context | null = null;

/**
 * Données structurées schema.org : éditeur, logiciel et ses offres, FAQ. Lues
 * par Google (résultats enrichis) et par les moteurs IA, qui citent plus
 * volontiers une réponse structurée et datée.
 */
function structuredData(): object {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: BRAND_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/favicon.svg`,
        email: LEGAL.contactEmail ?? undefined,
        legalName: LEGAL.companyName,
        identifier: { '@type': 'PropertyValue', propertyID: 'SIRET', value: LEGAL.siret },
        address: { '@type': 'PostalAddress', streetAddress: '25 rue des Vinaigriers', postalCode: '75010', addressLocality: 'Paris', addressCountry: 'FR' },
        contactPoint: [
          { '@type': 'ContactPoint', contactType: 'customer support', email: LEGAL.supportEmail ?? undefined, url: `${SITE_URL}/aide`, availableLanguage: 'French' },
        ],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}/#software`,
        name: BRAND_NAME,
        url: SITE_URL,
        description: DEFAULT_DESCRIPTION,
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Facturation électronique',
        operatingSystem: 'Web, iOS, Android',
        inLanguage: 'fr-FR',
        areaServed: 'FR',
        publisher: { '@id': `${SITE_URL}/#organization` },
        featureList: [
          'Facturation électronique conforme à la réforme 2026 (Peppol, Factur-X)',
          'Devis et factures rédigés par IA à partir d’un brief écrit ou vocal',
          'Lecture automatique des tickets et justificatifs de dépenses',
          'Signature des devis sur place ou en ligne, acompte payé par carte (Stripe)',
          'Planning, page de prise de rendez-vous et rappels automatiques',
          'Relances automatiques des devis, acomptes et factures',
          'Clients professionnels (Peppol) et particuliers (email)',
          'Tableau de bord, livre des recettes, export comptable FEC',
        ],
        offers: [
          { '@type': 'Offer', name: 'Essai gratuit', price: 0, priceCurrency: 'EUR', description: `${TRIAL_DAYS} jours, ${TRIAL_INVOICE_LIMIT} factures, sans carte bancaire` },
          ...(['essential', 'pro', 'expert'] as const).map((id) => ({
            '@type': 'Offer',
            name: PLANS[id].label,
            price: PLANS[id].priceEur,
            priceCurrency: 'EUR',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: PLANS[id].priceEur, priceCurrency: 'EUR', unitText: 'mois', valueAddedTaxIncluded: false },
          })),
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        mainEntity: faqs.map(([question, answer]) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })),
      },
    ],
  };
}

onMounted(() => {
  setJsonLd('landing', structuredData());
  if (!auth.user) {
    api
      .registrationStatus()
      .then((status) => (registrationOpen.value = status.open))
      .catch(() => (registrationOpen.value = false));
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  context = gsap.context(() => {
    gsap
      .timeline({ defaults: { ease: 'power3.out' } })
      .from('.nav-shell', { y: -18, opacity: 0, duration: 0.7 })
      .from('.hero-copy > *', { y: 28, opacity: 0, duration: 0.75, stagger: 0.1 }, '-=0.35')
      .from('.invoice-card', { y: 35, rotate: 1.5, opacity: 0, duration: 0.9 }, '-=0.75')
      .from('.floating-stat', { scale: 0.9, opacity: 0, duration: 0.6, stagger: 0.12 }, '-=0.4');

    gsap.fromTo('.orbit', { strokeDashoffset: 900 }, { strokeDashoffset: 0, duration: 2.2, stagger: 0.2, ease: 'power2.inOut' });
    gsap.to('.paid-stamp svg', { rotate: 8, duration: 1.8, repeat: -1, yoyo: true, ease: 'sine.inOut' });

    gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((element) => {
      gsap.from(element, {
        scrollTrigger: { trigger: element, start: 'top 86%', once: true },
        y: 34,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
      });
    });
  }, root.value ?? undefined);
});

// Sans ce nettoyage, les déclencheurs de défilement survivraient à la page.
onBeforeUnmount(() => {
  context?.revert();
  setJsonLd('landing', null);
});

function scrollTo(id: string): void {
  document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  menuOpen.value = false;
}
</script>

<template>
  <div ref="root" id="top" class="landing">
    <header class="site-header">
      <nav class="nav-shell" aria-label="Navigation principale">
        <a class="logo" href="#top" @click.prevent="scrollTo('#top')" :aria-label="`${BRAND_NAME}, accueil`"><BrandMark /><span>{{ BRAND_NAME }}</span></a>
        <div class="nav-links" :class="{ 'is-open': menuOpen }">
          <a href="#ai" @click.prevent="scrollTo('#ai')">IA</a>
          <a href="#product" @click.prevent="scrollTo('#product')">Fonctionnalités</a>
          <a href="#how" @click.prevent="scrollTo('#how')">Comment ça marche</a>
          <a href="#pricing" @click.prevent="scrollTo('#pricing')">Tarifs</a>
          <a href="#faq" @click.prevent="scrollTo('#faq')">Questions</a>
          <RouterLink to="/guides">Guides</RouterLink>
          <RouterLink v-if="!auth.user && registrationOpen" to="/login" class="nav-login">Se connecter</RouterLink>
          <button
            type="button"
            class="theme-toggle"
            :aria-label="theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'"
            :title="theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'"
            @click="toggleTheme"
          >
            <NavIcon :name="theme === 'dark' ? 'sun' : 'moon'" />
          </button>
          <RouterLink class="button button-small" :to="primaryCta.to">{{ primaryCta.label }}</RouterLink>
        </div>
        <button
          type="button"
          class="menu-button"
          :aria-label="menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'"
          :aria-expanded="menuOpen"
          @click="menuOpen = !menuOpen"
        >
          <LandingIcon :name="menuOpen ? 'x' : 'menu'" />
        </button>
      </nav>
    </header>

    <main>
      <section class="hero">
        <div class="hero-grid">
          <div class="hero-copy">
            <div class="eyebrow"><span></span> Devis et factures · assistant IA</div>
            <h1>Le brief du client.<br /><em>Votre devis, déjà rédigé.</em></h1>
            <p>
              Collez l'email du client, vos notes de rendez-vous, ou dictez un simple message vocal. Yakafa en tire un
              devis chiffré avec vos prix, signale ce qu'il a dû estimer, le fait accepter en ligne, puis le transforme en
              facture électronique conforme à la réforme 2026.
            </p>
            <div class="hero-actions">
              <RouterLink class="button" :to="primaryCta.to">{{ primaryCta.label }} <LandingIcon name="arrow" /></RouterLink>
              <button type="button" class="text-action" @click="scrollTo('#ai')">Découvrir les avantages de l'IA</button>
            </div>
            <div class="reassurance">
              <span><LandingIcon name="check" :size="16" /> {{ TRIAL_DAYS }} jours d'essai gratuit</span>
              <span><LandingIcon name="check" :size="16" /> Assistant IA inclus dans l'essai</span>
              <span><LandingIcon name="check" :size="16" /> PDF Factur-X</span>
              <span><LandingIcon name="check" :size="16" /> Réseau Peppol</span>
              <span><LandingIcon name="check" :size="16" /> Hébergé dans l'Union européenne</span>
            </div>
          </div>

          <div class="hero-visual" aria-label="Aperçu d'un devis rédigé par Yakafa à partir d'un brief client (exemple fictif)">
            <svg class="orbit-lines" viewBox="0 0 640 560" aria-hidden="true">
              <path class="orbit" d="M28 182C125 37 332 21 454 93c107 63 145 212 88 313-44 78-136 120-226 118" />
              <path class="orbit" d="M92 506c-78-104-62-269 25-360 79-82 218-111 323-58" />
              <circle cx="96" cy="121" r="7" fill="#f8b84e" />
              <circle cx="553" cy="387" r="5" fill="#2f9e8f" />
            </svg>

            <div class="invoice-card">
              <div class="invoice-top">
                <BrandMark />
                <span>DEVIS</span>
              </div>
              <div class="invoice-meta">
                <div><small>CLIENT</small><strong>Atelier Panache</strong><span>Nantes, France</span></div>
                <div><small>N° DE DEVIS</small><strong>DEV-2026-0012</strong><span>Valable 30 jours</span></div>
              </div>
              <div class="invoice-table">
                <div class="invoice-row heading"><span>PRESTATION</span><span>MONTANT HT</span></div>
                <div class="invoice-row"><span>3 ateliers en entreprise (2 h)</span><strong>1 050,00 €</strong></div>
                <div class="invoice-row"><span>Déplacement à Nantes <em class="estimated">prix estimé</em></span><strong>120,00 €</strong></div>
              </div>
              <div class="invoice-total"><span>Total TTC</span><strong>1 404,00 €</strong></div>
              <div class="paid-stamp">
                <svg viewBox="0 0 54 54" aria-hidden="true">
                  <circle cx="27" cy="27" r="24" class="stamp-bg" />
                  <path d="m17 28 7 7 14-17" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span><strong>Accepté en ligne</strong> par le client</span>
              </div>
            </div>

            <div class="floating-stat stat-income">
              <span class="mini-icon"><LandingIcon name="check" :size="18" /></span>
              <span><small>Prix repris de</small><strong>votre catalogue</strong></span>
            </div>
            <!-- Le brief d'où vient le devis : c'est la promesse de la page. -->
            <div class="floating-stat stat-prompt">
              <span class="mini-icon mini-sun"><LandingIcon name="spark" :size="18" /></span>
              <span><small>Brief du client</small><strong>« 3 ateliers de 2 h en novembre, à Nantes »</strong></span>
            </div>
          </div>
        </div>
        <div class="trust-row">
          <span>Pensé pour la réforme française :</span>
          <strong>réception obligatoire depuis le 1er septembre 2026</strong>
          <span class="trust-end">émission obligatoire au plus tard en septembre 2027</span>
        </div>
      </section>

      <section id="ai" class="ai-section">
        <div class="section-heading" data-reveal>
          <div>
            <span class="kicker">L'assistant devis</span>
            <h2>Du brief au devis<br />en quelques secondes.</h2>
          </div>
          <p>
            Vous recevez une demande, Yakafa la chiffre avec vos tarifs. Vous relisez, vous envoyez : rien ne part sans
            votre validation.
          </p>
        </div>

        <!-- Démonstration : un brief réaliste et le devis qui en sort. Exemple fictif, conforme au produit. -->
        <div class="brief-demo" data-reveal>
          <div class="brief-input">
            <span class="brief-label"><LandingIcon name="document" :size="14" /> Brief reçu par email</span>
            <p>
              « Bonjour, nous aimerions organiser trois ateliers de prise de parole de 2 h pour 15 collaborateurs, en
              novembre, dans nos locaux de Nantes. Pouvez-vous nous faire une proposition ? »
            </p>
            <span class="brief-soon"><LandingIcon name="spark" :size="14" /> Ou dictez-le, ou importez le message vocal du client</span>
          </div>
          <div class="brief-arrow" aria-hidden="true"><LandingIcon name="arrow" /></div>
          <div class="brief-output">
            <span class="brief-label"><LandingIcon name="quote" :size="14" /> Devis proposé</span>
            <div class="brief-line"><span>Atelier prise de parole (2 h) × 3</span><strong>1 050,00 €</strong></div>
            <div class="brief-line"><span>Déplacement à Nantes <em class="estimated">prix estimé</em></span><strong>120,00 €</strong></div>
            <div class="brief-line muted"><span>Validité 30 jours · acompte 30 % · supports inclus</span></div>
            <p class="brief-why">
              « Ateliers au tarif de votre catalogue. Le déplacement n'y figure pas : prix estimé, à vérifier. »
            </p>
          </div>
        </div>
        <div class="ai-grid">
          <article v-for="feature in aiFeatures" :key="feature.title" class="ai-card" data-reveal>
            <span class="feature-icon"><LandingIcon :name="feature.icon" /></span>
            <h3>{{ feature.title }}</h3>
            <p class="ai-prompt"><LandingIcon name="spark" :size="14" /> {{ feature.prompt }}</p>
            <p class="ai-result"><LandingIcon name="check" :size="14" /> {{ feature.result }}</p>
          </article>
        </div>
        <p class="ai-note" data-reveal>
          <LandingIcon name="shield" :size="16" />
          Les montants sont toujours recalculés par Yakafa, jamais repris tels quels de l'IA. Vos données servent à
          rédiger, pas à entraîner un modèle.
        </p>
      </section>

      <section id="product" class="features-section">
        <div class="section-heading" data-reveal>
          <div>
            <span class="kicker">Et tout le reste, bien fait</span>
            <h2>Moins d'administratif.<br />Plus de temps pour votre métier.</h2>
          </div>
          <p>Yakafa prend en charge ce qui vous ralentit, sans transformer votre activité en usine à gaz.</p>
        </div>
        <div class="feature-grid">
          <article v-for="feature in features" :key="feature.number" class="feature" data-reveal>
            <div class="feature-top">
              <span class="feature-icon"><LandingIcon :name="feature.icon" /></span>
              <span class="feature-number">{{ feature.number }}</span>
            </div>
            <h3>{{ feature.title }} <span v-if="feature.pro" class="pro-badge">Pro</span></h3>
            <p>{{ feature.text }}</p>
          </article>
        </div>
      </section>

      <section id="how" class="workflow-section">
        <div class="workflow-visual" data-reveal>
          <div class="app-window" aria-hidden="true">
            <div class="app-sidebar">
              <BrandMark />
              <span class="active-line"></span><span></span><span></span>
            </div>
            <div class="app-content">
              <div class="app-title"><strong>Tableau de bord</strong><small>Votre activité de septembre.</small></div>
              <div class="metrics">
                <div><small>Chiffre d'affaires</small><strong>4 820 €</strong><em>+18 %</em></div>
                <div><small>Encours Peppol</small><strong>1 240 €</strong><em>2 factures</em></div>
              </div>
              <div class="chart">
                <i v-for="(height, index) in [35, 58, 47, 74, 68, 92, 82]" :key="index" :style="{ height: `${height}%` }"></i>
              </div>
            </div>
          </div>
          <div class="sync-badge"><LandingIcon name="spark" :size="18" /> Statuts à jour</div>
        </div>
        <div class="workflow-copy" data-reveal>
          <span class="kicker light">Du rendez-vous au paiement</span>
          <h2>Vous facturez.<br />Yakafa suit le rythme.</h2>
          <p>
            De la saisie au paiement, chaque étape s'enchaîne. Les retours du réseau arrivent d'eux-mêmes, et vos
            chiffres restent à jour.
          </p>
          <ul>
            <li><LandingIcon name="check" /> Rendez-vous réservé en ligne, trajet compris</li>
            <li><LandingIcon name="check" /> Devis dicté sur place, signé au doigt, acompte payé par carte</li>
            <li><LandingIcon name="check" /> Facture envoyée en un clic : Peppol pour les pros, email pour les particuliers</li>
            <li><LandingIcon name="check" /> Relances automatiques, encaissements suivis, export FEC</li>
          </ul>
          <RouterLink class="button button-light" :to="primaryCta.to">{{ primaryCta.label }} <LandingIcon name="arrow" /></RouterLink>
        </div>
      </section>

      <section id="pricing" class="pricing-section">
        <div class="pricing-copy" data-reveal>
          <span class="kicker">Des tarifs clairs</span>
          <h2>Commencez gratuitement.<br />Choisissez ensuite.</h2>
          <p>
            L'essentiel de la facturation dans chaque offre ; le Pro ajoute l'encaissement en ligne et la prise de
            rendez-vous. Les quotas repartent le 1er de chaque mois, et la saisie à la main reste toujours possible.
          </p>
        </div>
        <div class="offer-grid">
          <div v-for="offer in offers" :key="offer.id" class="price-card" :class="{ 'is-highlight': offer.highlight }" data-reveal>
            <span v-if="offer.id === 'trial'" class="popular">SANS CARTE BANCAIRE</span>
            <span v-else-if="offer.id === 'expert'" class="popular">NOUVEAU</span>
            <p>{{ offer.name }}</p>
            <div class="price">
              <strong>{{ offer.price }}</strong><span>{{ offer.period }}</span>
            </div>
            <ul>
              <li v-for="item in offer.items" :key="item"><LandingIcon name="check" /> {{ item }}</li>
            </ul>
            <RouterLink v-if="offer.id === 'trial'" class="button" :to="primaryCta.to">{{ primaryCta.label }} <LandingIcon name="arrow" /></RouterLink>
          </div>
        </div>
        <small class="pricing-note" data-reveal>
          Envoi Peppol, PDF Factur-X, devis signés en ligne ou sur place, clients particuliers, recettes, dépenses,
          export FEC et bilan micro / réel inclus dans toutes les offres. L'essai donne accès à tout, sauf le numéro dédié de l'offre Expert. Prix hors taxes.
        </small>
      </section>

      <section id="faq" class="faq-section">
        <div class="faq-heading" data-reveal>
          <span class="kicker">Questions fréquentes</span>
          <h2>Vous vous demandez peut-être…</h2>
        </div>
        <div class="faq-list" data-reveal>
          <div v-for="([question, answer], index) in faqs" :key="question" class="faq-item" :class="{ 'is-open': openFaq === index }">
            <button type="button" :aria-expanded="openFaq === index" @click="openFaq = openFaq === index ? -1 : index">
              <span>{{ question }}</span><LandingIcon name="chevron" />
            </button>
            <div class="faq-answer"><p>{{ answer }}</p></div>
          </div>
        </div>
      </section>

      <section id="guides" class="guides-section">
        <div class="guides-heading" data-reveal>
          <span class="kicker">Guides</span>
          <h2>Tout comprendre de la facture électronique</h2>
        </div>
        <div class="guides-grid" data-reveal>
          <RouterLink v-for="guide in GUIDES" :key="guide.slug" :to="`/guides/${guide.slug}`" class="guide-card">
            <strong>{{ guide.shortTitle }}</strong>
            <span>{{ guide.description }}</span>
            <em>Lire le guide <LandingIcon name="arrow" /></em>
          </RouterLink>
        </div>
      </section>

      <section class="final-cta" data-reveal>
        <div>
          <span class="kicker">Prêt pour la réforme ?</span>
          <h2>Votre prochain devis<br />est déjà dans le brief.</h2>
        </div>
        <RouterLink class="button button-deep" :to="primaryCta.to">{{ primaryCta.label }} <LandingIcon name="arrow" /></RouterLink>
        <svg class="cta-line" viewBox="0 0 420 160" aria-hidden="true">
          <path d="M8 138C98 8 268 12 408 84" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="7 10" />
        </svg>
      </section>
    </main>

    <footer>
      <a class="logo" href="#top" @click.prevent="scrollTo('#top')"><BrandMark /><span>{{ BRAND_NAME }}</span></a>
      <p>La facturation électronique qui s'écrit toute seule.</p>
      <div>
        <a href="#ai" @click.prevent="scrollTo('#ai')">IA</a><a href="#product" @click.prevent="scrollTo('#product')">Fonctionnalités</a><a href="#pricing" @click.prevent="scrollTo('#pricing')">Tarifs</a><a href="#faq" @click.prevent="scrollTo('#faq')">Questions</a><RouterLink to="/guides">Guides</RouterLink><RouterLink to="/aide">Aide</RouterLink><RouterLink to="/login">Connexion</RouterLink>
      </div>
      <small class="footer-legal">
        <span>© 2026 {{ LEGAL.companyName }} — {{ BRAND_NAME }}. Factures transmises via le réseau Peppol. IA : Claude, d'Anthropic.</span>
        <span>
          <RouterLink to="/mentions-legales">Mentions légales</RouterLink>
          <RouterLink to="/cgu">Conditions générales</RouterLink>
          <RouterLink to="/confidentialite">Confidentialité</RouterLink>
          <a href="#" @click.prevent="openConsentPanel">Gérer les cookies</a>
        </span>
      </small>
    </footer>
  </div>
</template>

<style scoped>
/*
 * Palette de la maquette transposée à Yakafa : mint #A2E8DD en accent,
 * vert profond #0E3B35 là où la maquette avait son bleu marine. Le mint ne
 * porte jamais de texte clair : trop pâle, il reçoit le vert profond.
 */
.landing {
  --mint: #a2e8dd;
  --mint-hover: #86dccf;
  --mint-soft: #e4f7f3;
  --deep: #0e3b35;
  --accent-text: #1f6f64;
  --emphasis: #2f9e8f;
  --ink: #14211f;
  --muted: #5d6b69;
  --cream: #fbfaf7;
  --surface: #ffffff;
  --line: #dfe6e4;
  --line-strong: #cdd6d4;
  --sun: #f8b84e;
  --section-deep: #0e3b35;
  --paid: #168653;
  --paid-bg: #e8f6ee;
  --shadow: rgba(14, 59, 53, 0.14);

  min-height: 100vh;
  background: var(--cream);
  color: var(--ink);
  font-family: 'Manrope Variable', 'Manrope', system-ui, sans-serif;
  font-synthesis: none;
}

/* Sélecteur entièrement global : en CSS scopé, `:global(html.dark) .landing`
   ne garderait que `html.dark` et perdrait `.landing`. */
:global(html.dark .landing) {
  --mint-soft: rgba(162, 232, 221, 0.12);
  --deep: #eafaf6;
  --accent-text: #a2e8dd;
  --emphasis: #a2e8dd;
  --ink: #e5e7eb;
  --muted: #94a3b8;
  --cream: #0b0f17;
  --surface: #111822;
  --line: #2a3444;
  --line-strong: #3a4659;
  --section-deep: #0f2c28;
  --paid: #6ee7b7;
  --paid-bg: rgba(6, 78, 59, 0.5);
  --shadow: rgba(0, 0, 0, 0.45);
}

.landing :where(a) { color: inherit; text-decoration: none; }
.landing :where(button, a) { min-height: 0; font: inherit; }

.site-header { position: absolute; z-index: 20; top: 0; left: 0; width: 100%; }
.nav-shell { max-width: 1240px; height: 82px; margin: 0 auto; padding: 0 28px; display: flex; align-items: center; justify-content: space-between; }
.logo { display: inline-flex; align-items: center; gap: 10px; color: var(--deep); font-size: 21px; font-weight: 800; letter-spacing: -0.8px; }
.logo :deep(svg) { width: 34px; height: 34px; }
.nav-links { display: flex; align-items: center; gap: 28px; font-size: 13px; font-weight: 600; }
.nav-links a:not(.button):hover { color: var(--accent-text); }
.theme-toggle { width: 38px; height: 38px; display: grid; place-items: center; border: 1px solid var(--line); border-radius: 10px; background: var(--surface); color: var(--ink); }
.theme-toggle:hover { border-color: var(--line-strong); }
.button { border: 0; min-height: 52px; padding: 0 22px; display: inline-flex; align-items: center; justify-content: center; gap: 14px; border-radius: 10px; background: var(--mint); color: #0e3b35; font-size: 14px; font-weight: 700; box-shadow: 0 8px 24px rgba(47, 158, 143, 0.22); transition: transform 0.2s, box-shadow 0.2s, background 0.2s; }
.button:hover { transform: translateY(-2px); background: var(--mint-hover); box-shadow: 0 12px 28px rgba(47, 158, 143, 0.3); }
.button-small { min-height: 42px; padding: 0 17px; font-size: 12px; box-shadow: none; }
.menu-button { display: none; border: 0; background: transparent; color: var(--deep); }

.hero { min-height: 780px; padding: 154px 28px 0; overflow: hidden; background: radial-gradient(circle at 78% 22%, rgba(162, 232, 221, 0.28), transparent 32%), var(--cream); }
.hero-grid { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 0.96fr 1.04fr; gap: 44px; align-items: center; }
.eyebrow, .kicker { color: var(--accent-text); font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; }
.eyebrow { display: flex; align-items: center; gap: 9px; }
.eyebrow span { width: 18px; height: 2px; background: var(--sun); }
.hero h1 { max-width: 600px; margin: 22px 0 24px; color: var(--deep); font-size: clamp(46px, 5.2vw, 74px); font-weight: 800; line-height: 0.99; letter-spacing: -0.064em; }
.hero h1 em { color: var(--emphasis); font-style: normal; }
.hero-copy > p { max-width: 540px; margin: 0; color: var(--muted); font-size: 17px; line-height: 1.75; }
.hero-actions { margin-top: 34px; display: flex; align-items: center; gap: 27px; }
.text-action { border: 0; padding: 8px 0; border-bottom: 1px solid var(--line-strong); background: transparent; color: var(--ink); font-size: 13px; font-weight: 700; cursor: pointer; }
.reassurance { margin-top: 24px; display: flex; flex-wrap: wrap; gap: 17px; color: var(--muted); font-size: 11px; }
.reassurance span { display: flex; align-items: center; gap: 5px; }
.reassurance svg { color: var(--paid); }

.hero-visual { position: relative; height: 540px; }
.orbit-lines { position: absolute; inset: -25px -40px 0 -60px; width: calc(100% + 100px); height: calc(100% + 40px); pointer-events: none; }
.orbit { fill: none; stroke: var(--line-strong); stroke-width: 1.2; stroke-dasharray: 8 9; }
.invoice-card { position: absolute; z-index: 2; top: 28px; left: 50%; width: min(386px, 74%); min-height: 446px; padding: 30px 32px; transform: translateX(-50%) rotate(-1.5deg); border-radius: 14px; background: var(--surface); box-shadow: 0 30px 80px var(--shadow), 0 2px 8px var(--shadow); }
.invoice-top { display: flex; align-items: center; justify-content: space-between; padding-bottom: 23px; border-bottom: 1px solid var(--line); }
.invoice-top :deep(svg) { width: 28px; height: 28px; }
.invoice-top > span { color: var(--accent-text); font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.17em; }
.invoice-meta { padding: 28px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
.invoice-meta div { display: flex; flex-direction: column; gap: 5px; }
.invoice-meta small, .invoice-meta span, .invoice-row.heading { font-size: 8px; }
.invoice-meta small { color: var(--muted); font-family: 'DM Mono', monospace; letter-spacing: 0.1em; }
.invoice-meta strong { color: var(--deep); font-size: 11px; }
.invoice-meta span { color: var(--muted); }
.invoice-table { border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
.invoice-row { min-height: 38px; display: grid; grid-template-columns: 1fr auto; align-items: center; border-bottom: 1px solid var(--line); color: var(--muted); font-size: 9px; }
.invoice-row:last-child { border-bottom: 0; }
.invoice-row.heading { min-height: 30px; font-family: 'DM Mono', monospace; }
.invoice-row strong { color: var(--ink); }
.invoice-total { padding-top: 20px; display: flex; justify-content: flex-end; align-items: baseline; gap: 30px; font-size: 10px; }
.invoice-total strong { color: var(--deep); font-size: 20px; letter-spacing: -0.05em; }
.paid-stamp { position: absolute; right: 32px; bottom: 25px; display: flex; align-items: center; gap: 9px; color: var(--paid); }
.paid-stamp svg { width: 34px; height: 34px; }
.stamp-bg { fill: var(--paid-bg); }
.paid-stamp span { display: flex; flex-direction: column; color: var(--muted); font-size: 8px; }
.paid-stamp strong { color: var(--paid); font-size: 10px; }
.floating-stat { position: absolute; z-index: 3; display: flex; align-items: center; gap: 11px; border-radius: 12px; background: var(--surface); box-shadow: 0 16px 35px var(--shadow); }
.mini-icon { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 9px; background: var(--mint-soft); color: var(--accent-text); }
.mini-sun { background: #fff3dc; color: #b87500; }
.floating-stat > span:last-child { display: flex; flex-direction: column; }
.floating-stat small { color: var(--muted); font-size: 8px; }
.floating-stat strong { color: var(--deep); font-size: 13px; }
.stat-income { left: 0; bottom: 102px; padding: 13px 20px 13px 13px; }
/* Au-dessus de la facture, sans la masquer : la consigne précède le document. */
.stat-prompt { left: 50%; top: -34px; transform: translateX(-62%); padding: 11px 18px 11px 11px; white-space: nowrap; }
.stat-prompt strong { font-size: 12px; font-weight: 700; }
.trust-row { max-width: 1180px; margin: 55px auto 0; padding: 25px 0; display: flex; flex-wrap: wrap; align-items: center; gap: 9px; border-top: 1px solid var(--line); color: var(--muted); font-size: 11px; }
.trust-row strong { color: var(--ink); }
.trust-end { margin-left: auto; }

.features-section { max-width: 1180px; margin: 0 auto; padding: 125px 28px 140px; }

.ai-section { max-width: 1180px; margin: 0 auto; padding: 125px 28px 40px; }
.brief-demo { margin-top: 56px; display: grid; grid-template-columns: 1fr auto 1.1fr; align-items: center; gap: 22px; }
.brief-input, .brief-output { padding: 26px 28px; border: 1px solid var(--line); border-radius: 16px; background: var(--surface); box-shadow: 0 18px 50px var(--shadow); }
.brief-input p { margin: 12px 0 0; color: var(--deep); font-size: 14px; font-style: italic; line-height: 1.75; }
.brief-label { display: inline-flex; align-items: center; gap: 7px; color: var(--accent-text); font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
.brief-soon { margin-top: 16px; display: flex; align-items: center; gap: 7px; color: var(--muted); font-size: 11px; }
.brief-arrow { width: 44px; height: 44px; display: grid; place-items: center; border-radius: 50%; background: var(--mint); color: #0e3b35; }
.brief-line { padding: 11px 0; display: flex; justify-content: space-between; gap: 14px; border-bottom: 1px solid var(--line); color: var(--ink); font-size: 13px; }
.brief-line.muted { color: var(--muted); font-size: 11px; border-bottom: 0; }
.brief-line:first-of-type { margin-top: 8px; }
.brief-why { margin: 10px 0 0; padding: 10px 12px; border-radius: 10px; background: var(--mint-soft); color: var(--deep); font-size: 12px; line-height: 1.6; }
.estimated { margin-left: 6px; padding: 1px 6px; border-radius: 6px; background: #fff3dc; color: #8a5a00; font-size: 0.85em; font-style: normal; font-weight: 600; white-space: nowrap; }
.ai-grid { margin-top: 60px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.ai-card { padding: 30px; display: flex; flex-direction: column; gap: 16px; border: 1px solid var(--line); border-radius: 16px; background: var(--surface); box-shadow: 0 18px 50px var(--shadow); }
.ai-card h3 { margin: 6px 0 0; color: var(--deep); font-size: 19px; font-weight: 750; letter-spacing: -0.025em; }
.ai-card p { margin: 0; display: flex; gap: 9px; font-size: 13px; line-height: 1.7; }
.ai-card p svg { flex: none; margin-top: 4px; }
.ai-prompt { padding: 12px 14px; border-radius: 10px; background: var(--mint-soft); color: var(--deep); font-style: italic; }
.ai-prompt svg { color: var(--accent-text); }
.ai-result { color: var(--muted); }
.ai-result svg { color: var(--paid); }
.ai-note { margin: 34px 0 0; display: flex; align-items: center; gap: 10px; color: var(--muted); font-size: 12px; }
.ai-note svg { flex: none; color: var(--accent-text); }
.section-heading { display: grid; grid-template-columns: 1.3fr 0.7fr; align-items: end; gap: 80px; }
.section-heading h2, .workflow-copy h2, .faq-heading h2, .final-cta h2 { margin: 16px 0 0; color: var(--deep); font-size: clamp(35px, 4vw, 54px); font-weight: 800; line-height: 1.08; letter-spacing: -0.05em; }
.section-heading p { margin: 0 0 5px; color: var(--muted); font-size: 14px; line-height: 1.8; }
.feature-grid { margin-top: 70px; display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--line-strong); border-bottom: 1px solid var(--line-strong); }
.feature { padding: 34px 34px 40px; border-right: 1px solid var(--line-strong); }
.feature:nth-child(3n + 1) { padding-left: 0; }
.feature:nth-child(3n) { padding-right: 0; border-right: 0; }
.feature:nth-child(n + 4) { border-top: 1px solid var(--line-strong); }
.pro-badge { margin-left: 6px; padding: 2px 7px; border-radius: 999px; background: var(--mint-soft); color: var(--accent-text); font-family: 'DM Mono', monospace; font-size: 10px; font-weight: 500; letter-spacing: 0.04em; vertical-align: middle; }
.feature-top { display: flex; align-items: center; justify-content: space-between; }
.feature-icon { width: 46px; height: 46px; display: grid; place-items: center; border-radius: 11px; background: var(--mint-soft); color: var(--accent-text); }
.feature-number { color: var(--muted); font-family: 'DM Mono', monospace; font-size: 10px; }
.feature h3 { margin: 30px 0 14px; color: var(--deep); font-size: 19px; letter-spacing: -0.025em; }
.feature p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.75; }

.workflow-section { min-height: 670px; padding: 90px max(28px, calc((100vw - 1180px) / 2)); display: grid; grid-template-columns: 1.08fr 0.92fr; align-items: center; gap: 90px; overflow: hidden; background: var(--section-deep); color: #fff; }
.workflow-visual { position: relative; min-height: 435px; display: flex; align-items: center; }
.app-window { width: 100%; min-height: 385px; display: grid; grid-template-columns: 105px 1fr; overflow: hidden; border-radius: 14px; background: #f7f8fa; box-shadow: 0 30px 80px rgba(2, 13, 29, 0.35); }
.app-sidebar { padding: 23px 18px; display: flex; flex-direction: column; gap: 18px; background: #fff; }
.app-sidebar :deep(svg) { width: 26px; height: 26px; }
.app-sidebar > span { width: 55px; height: 7px; border-radius: 4px; background: #e6e9ee; }
.app-sidebar .active-line { margin-top: 30px; background: #a2e8dd; }
.app-content { padding: 38px; color: #14211f; }
.app-title { display: flex; flex-direction: column; gap: 5px; }
.app-title strong { font-size: 19px; }
.app-title small { color: #8a9795; font-size: 9px; }
.metrics { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.metrics div { padding: 20px; display: grid; grid-template-columns: 1fr auto; gap: 8px; border-radius: 10px; background: #fff; }
.metrics small { grid-column: 1 / -1; color: #8a9795; font-size: 8px; }
.metrics strong { font-size: 19px; }
.metrics em { color: #168653; font-size: 8px; font-style: normal; }
.chart { height: 125px; margin-top: 24px; padding: 20px 25px 0; display: flex; align-items: end; gap: 16px; border-radius: 10px; background: #fff; }
.chart i { flex: 1; border-radius: 4px 4px 0 0; background: #cdeee8; }
.chart i:nth-child(6) { background: #2f9e8f; }
.sync-badge { position: absolute; right: -24px; bottom: 13px; padding: 14px 18px; display: flex; align-items: center; gap: 9px; border-radius: 10px; background: var(--sun); color: #513505; font-size: 11px; font-weight: 700; box-shadow: 0 16px 30px rgba(0, 0, 0, 0.18); }
.workflow-copy h2 { color: #fff; }
.kicker.light { color: #a2e8dd; }
.workflow-copy > p { margin: 25px 0; color: #b9d3cf; font-size: 14px; line-height: 1.8; }
.workflow-copy ul { margin: 0 0 30px; padding: 0; list-style: none; display: grid; gap: 13px; }
.workflow-copy li { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.workflow-copy li svg { color: #a2e8dd; }
.button-light { background: #fff; color: #0e3b35; box-shadow: none; }
.button-light:hover { background: #effbf9; }

.pricing-section { max-width: 1180px; margin: 0 auto; padding: 130px 28px 60px; }
.offer-grid { margin-top: 50px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; align-items: stretch; }
.offer-grid .price-card { display: flex; flex-direction: column; }
.offer-grid .price-card ul { flex: 1; }
.price-card.is-highlight { border-color: var(--mint); box-shadow: 0 24px 70px var(--shadow), 0 0 0 1px var(--mint); }
.pricing-note { margin-top: 26px; display: block; color: var(--muted); font-size: 11px; text-align: center; }
.pricing-copy h2 { margin: 16px 0 0; color: var(--deep); font-size: clamp(35px, 4vw, 54px); font-weight: 800; line-height: 1.08; letter-spacing: -0.05em; }
.pricing-copy > p { max-width: 460px; margin: 25px 0 0; color: var(--muted); font-size: 14px; line-height: 1.8; }
.price-card { position: relative; padding: 34px 28px; border: 1px solid var(--line); border-radius: 18px; background: var(--surface); box-shadow: 0 24px 70px var(--shadow); }
.popular { position: absolute; top: 0; right: 0; padding: 9px 14px; border-radius: 0 18px 0 12px; background: var(--mint-soft); color: var(--accent-text); font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.08em; }
.price-card > p { margin: 0 0 18px; color: var(--deep); font-size: 14px; font-weight: 700; }
.price { padding-bottom: 25px; display: flex; align-items: center; gap: 18px; border-bottom: 1px solid var(--line); }
.price strong { color: var(--deep); font-size: 54px; line-height: 1; letter-spacing: -0.06em; }
.price span { color: var(--muted); font-size: 10px; line-height: 1.6; }
.price-card ul { margin: 28px 0; padding: 0; list-style: none; display: grid; gap: 13px; }
.price-card li { display: flex; align-items: center; gap: 10px; color: var(--muted); font-size: 13px; }
.price-card li svg { color: var(--paid); }
.price-card .button { width: 100%; }
.price-card > small { margin-top: 15px; display: block; text-align: center; color: var(--muted); font-size: 10px; }
.nav-login { font-weight: 700; }

.faq-section { max-width: 1000px; margin: 0 auto; padding: 130px 28px; display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 90px; }
.faq-heading h2 { font-size: 38px; }
.faq-list { border-top: 1px solid var(--line-strong); }
.faq-item { border-bottom: 1px solid var(--line-strong); }
.faq-item button { width: 100%; padding: 24px 0; border: 0; display: flex; align-items: center; justify-content: space-between; gap: 20px; background: transparent; color: var(--deep); text-align: left; font-size: 14px; font-weight: 700; cursor: pointer; }
.faq-item button svg { flex: none; transition: transform 0.25s; }
.faq-item.is-open button svg { transform: rotate(180deg); }
.faq-answer { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.3s ease; }
.faq-answer p { min-height: 0; margin: 0; overflow: hidden; color: var(--muted); font-size: 13px; line-height: 1.8; }
.faq-item.is-open .faq-answer { grid-template-rows: 1fr; }
.faq-item.is-open .faq-answer p { padding-bottom: 24px; }

.final-cta { position: relative; max-width: 1180px; min-height: 315px; margin: 0 auto 90px; padding: 62px 70px; display: flex; align-items: center; justify-content: space-between; gap: 40px; overflow: hidden; border-radius: 20px; background: #a2e8dd; color: #0e3b35; }
.final-cta .kicker { color: #1f6f64; }
.final-cta h2 { color: #0e3b35; font-size: 43px; }
.button-deep { z-index: 2; flex: none; background: #0e3b35; color: #fff; box-shadow: none; }
.button-deep:hover { background: #164d46; }
.cta-line { position: absolute; right: -20px; bottom: -20px; width: 420px; color: rgba(14, 59, 53, 0.25); }

footer { max-width: 1180px; margin: 0 auto; padding: 0 28px 40px; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 28px; color: var(--muted); font-size: 11px; }
footer p { margin: 0; }
footer div { display: flex; gap: 22px; color: var(--ink); }
footer small { grid-column: 1 / -1; padding-top: 20px; border-top: 1px solid var(--line); }
.footer-legal { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px 22px; }
.footer-legal span:last-child { display: flex; flex-wrap: wrap; gap: 18px; }
.footer-legal a:hover { color: var(--accent-text); }

.guides-section { max-width: 1180px; margin: 0 auto; padding: 40px 28px 110px; }
.guides-heading h2 { margin: 16px 0 0; color: var(--deep); font-size: clamp(30px, 3vw, 40px); font-weight: 800; line-height: 1.1; letter-spacing: -0.04em; }
.guides-grid { margin-top: 36px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.guide-card { display: flex; flex-direction: column; gap: 10px; padding: 26px; border: 1px solid var(--line); border-radius: 18px; background: var(--surface); color: inherit; text-decoration: none; transition: border-color 0.2s, transform 0.2s; }
.guide-card:hover { border-color: var(--mint); transform: translateY(-2px); }
.guide-card strong { color: var(--deep); font-size: 16px; line-height: 1.3; }
.guide-card span { flex: 1; color: var(--muted); font-size: 13px; line-height: 1.6; }
.guide-card em { display: inline-flex; align-items: center; gap: 6px; color: var(--accent-text); font-size: 12px; font-style: normal; font-weight: 700; }

/* Écrans larges mais peu hauts (MacBook 13-14 pouces, ~860 px utiles) : la grille
   des tarifs doit tenir en entier sous l'en-tête, boutons compris. */
@media (min-width: 901px) and (max-height: 960px) {
  .pricing-section { padding: 96px 28px 40px; }
  .pricing-copy h2 { font-size: clamp(32px, 3.2vw, 44px); }
  .pricing-copy > p { margin-top: 14px; }
  .offer-grid { margin-top: 30px; gap: 18px; }
  .price-card { padding: 28px 30px; }
  .price-card > p { margin-bottom: 12px; }
  .price { padding-bottom: 16px; }
  .price strong { font-size: 44px; }
  .price-card ul { margin: 18px 0; gap: 9px; }
  .pricing-note { margin-top: 16px; }
}

@media (min-width: 901px) and (max-width: 1180px) {
  .offer-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 900px) {
  .guides-grid { grid-template-columns: 1fr 1fr; }
  .nav-shell { height: 70px; }
  .menu-button { display: grid; place-items: center; }
  .nav-links { position: absolute; top: 70px; left: 20px; right: 20px; padding: 24px; display: none; align-items: stretch; flex-direction: column; gap: 18px; border-radius: 14px; background: var(--surface); box-shadow: 0 20px 50px var(--shadow); }
  .nav-links.is-open { display: flex; }
  .nav-links .button { width: 100%; }
  .hero { padding-top: 125px; }
  .hero-grid { grid-template-columns: 1fr; text-align: center; }
  .hero-copy { display: flex; flex-direction: column; align-items: center; }
  .hero-visual { width: min(620px, 100%); margin: 10px auto 0; }
  .trust-row { margin-top: 20px; justify-content: center; text-align: center; }
  .trust-end { margin-left: 0; }
  .section-heading, .workflow-section, .faq-section, .ai-grid, .offer-grid, .brief-demo { grid-template-columns: 1fr; }
  .brief-arrow { margin: 0 auto; transform: rotate(90deg); }
  .pricing-section { max-width: 650px; gap: 50px; }
  .section-heading { gap: 22px; }
  .workflow-section { padding-top: 80px; padding-bottom: 80px; gap: 60px; }
  .faq-section { gap: 45px; }
  .final-cta { margin-left: 20px; margin-right: 20px; }
}

@media (max-width: 640px) {
  .guides-grid { grid-template-columns: 1fr; }
  .guides-section { padding: 20px 20px 80px; }
  .nav-shell { padding: 0 18px; }
  .hero { min-height: auto; padding: 112px 18px 0; }
  .hero h1 { font-size: 44px; }
  .hero-copy > p { font-size: 15px; }
  .hero-actions { width: 100%; flex-direction: column; gap: 14px; }
  .hero-actions .button { width: 100%; }
  .reassurance { justify-content: center; }
  .hero-visual { height: 455px; margin-top: 20px; }
  .invoice-card { top: 25px; width: 78%; min-height: 370px; padding: 22px; }
  .invoice-meta { padding: 20px 0; }
  .invoice-total strong { font-size: 16px; }
  .paid-stamp { right: 20px; bottom: 18px; }
  .stat-income { left: -4px; bottom: 40px; }
  .stat-prompt { top: -26px; transform: translateX(-50%); }
  .ai-section { padding: 90px 20px 20px; }
  .ai-note { align-items: flex-start; }
  .features-section { padding: 90px 20px; }
  .feature-grid { grid-template-columns: 1fr; }
  .feature { padding: 30px 0; border-right: 0; border-bottom: 1px solid var(--line-strong); }
  .feature:last-child { border-bottom: 0; }
  .feature:nth-child(n + 4) { border-top: 0; }
  .workflow-section { padding-left: 20px; padding-right: 20px; gap: 45px; }
  .workflow-visual { min-height: 350px; }
  .app-window { min-height: 315px; grid-template-columns: 64px 1fr; }
  .app-sidebar { padding: 18px 12px; }
  .app-sidebar > span { width: 34px; }
  .app-content { padding: 24px 16px; }
  .metrics { grid-template-columns: 1fr; }
  .metrics div:nth-child(2) { display: none; }
  .chart { gap: 8px; padding: 16px 15px 0; }
  .sync-badge { right: -6px; }
  .faq-section { padding: 90px 20px; }
  .pricing-section { padding: 90px 20px 30px; }
  .price-card { padding: 34px 24px; }
  .final-cta { min-height: 330px; margin: 0 14px 70px; padding: 46px 28px; align-items: flex-start; flex-direction: column; justify-content: center; }
  .final-cta h2 { font-size: 34px; }
  .button-deep { width: 100%; }
  footer { padding: 0 20px 30px; grid-template-columns: 1fr; }
  footer div { flex-wrap: wrap; }
}

@media (prefers-reduced-motion: reduce) {
  .button:hover { transform: none; }
}
</style>
